import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useLocation } from "react-router-dom";
import { StarDecorations } from "components/ui/StarDecorations";
import { StepIndicator } from "components/ui/StepIndicator";
import { FormField } from "components/ui/FormField";
import { SelectField } from "components/ui/SelectField";
import { DateRangeInputs } from "components/ui/DateRangeInputs";
import { FormTextArea } from "components/ui/FormTextArea";
import { FormNavigation } from "components/ui/FormNavigation";
import { FormStep } from "components/ui/FormStep";
import { FormHeader } from "components/ui/FormHeader";
import { ManagerSelector } from "components/ui/ManagerSelector";
import { ReactComponent as Step1Icon } from "assets/img/2026/step1_elbb-cadastro.svg";
import { ReactComponent as Step3Icon } from "assets/img/2026/step3_elbb-cadastro.svg";
import { ReactComponent as Step2Icon } from "assets/img/2026/step2_complet.svg";
import characterFormELBB from "assets/img/2026/characterFormELBB.png";
import {
  formDataConexaoSchema,
  FormDataConexaoType,
} from "schemas/cadastro-conexao.schema";
import { getBlocosConexao, PerspectivaECBB } from "services/category.service";
import { findManagers } from "services/entrega.service";
import { useAuth } from "context/AuthContext";
import { salvarIniciativa, atualizarIniciativa } from "services/iniciativa-elbb.service";
import { SuccessCardELBB } from "components/ui/SuccessCardELBB";

export function CadastroConexao() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);
  const [blocosConexao, setBlocosConexao] = useState<PerspectivaECBB[]>([]);
  const [loadingBlocos, setLoadingBlocos] = useState(true);
  const [blocoSelecionado, setBlocoSelecionado] =
    useState<PerspectivaECBB | null>(null);
  const [gestores, setGestores] = useState<any[]>([]);
  const [loadingGestores, setLoadingGestores] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit: hookFormHandleSubmit,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useForm<FormDataConexaoType>({
    resolver: zodResolver(formDataConexaoSchema),
    defaultValues: {
      TITULO: "",
      BLOCO: "",
      INDICADOR_NOME: "",
      inicioEntrega: "",
      conclusaoEntrega: "",
      GESTOR_ATUAL_NOME: "",
      DESCRICAO: "",
      RESULTADO: "",
    },
  });

  const formData = watch();
  const blocoSelecionadoNome = watch("BLOCO");

  // Identificar modo de edição
  useEffect(() => {
    const editData = location.state?.editData;
    if (editData) {
      setIsEditMode(true);
      setEditingId(editData._id);
    }
  }, [location.state]);

  // Preencher dados de edição após blocos e gestores serem carregados
  useEffect(() => {
    const editData = location.state?.editData;
    if (editData && !loadingBlocos && !loadingGestores && blocosConexao.length > 0 && gestores.length > 0) {
      console.log("Dados para edição:", editData);
      console.log("Start date:", editData.start);
      console.log("End date:", editData.end);
      
      // Preencher campos básicos
      setValue("TITULO", editData.title || "");
      setValue("DESCRICAO", editData.description || "");
      setValue("RESULTADO", editData.result || "");
      
      // Preencher bloco
      if (editData.indicator?.bloco) {
        setValue("BLOCO", editData.indicator.bloco);
      }
      
      // Preencher indicador (após bloco)
      if (editData.indicator?.nome) {
        setTimeout(() => {
          setValue("INDICADOR_NOME", editData.indicator.nome);
        }, 100);
      }
      
      // Formatar datas para o input (YYYY-MM-DD)
      let startDate = "";
      let endDate = "";
      
      if (editData.start) {
        if (editData.start.$date) {
          startDate = new Date(editData.start.$date).toISOString().split('T')[0];
        } else if (typeof editData.start === 'string') {
          startDate = new Date(editData.start).toISOString().split('T')[0];
        } else if (typeof editData.start === 'number') {
          // Timestamp em milissegundos
          startDate = new Date(editData.start).toISOString().split('T')[0];
        }
      }
      
      if (editData.end) {
        if (editData.end.$date) {
          endDate = new Date(editData.end.$date).toISOString().split('T')[0];
        } else if (typeof editData.end === 'string') {
          endDate = new Date(editData.end).toISOString().split('T')[0];
        } else if (typeof editData.end === 'number') {
          // Timestamp em milissegundos
          endDate = new Date(editData.end).toISOString().split('T')[0];
        }
      }
      
      console.log("Datas formatadas - Início:", startDate, "Fim:", endDate);
      
      if (startDate) {
        setValue("inicioEntrega", startDate);
      }
      if (endDate) {
        setValue("conclusaoEntrega", endDate);
      }
      
      // Preencher gestor
      const nomeGestor = editData.original_manager_detail?.nome_guerra ||
                        editData.original_manager_detail?.nome ||
                        editData.original_manager;
      if (nomeGestor) {
        setValue("GESTOR_ATUAL_NOME", nomeGestor);
      }
    }
  }, [location.state, setValue, loadingBlocos, loadingGestores, blocosConexao, gestores]);

  useEffect(() => {
    const carregarBlocos = async () => {
      setLoadingBlocos(true);
      try {
        const blocos = await getBlocosConexao();
        setBlocosConexao(blocos);
      } catch (error) {
        console.error("Erro ao carregar blocos:", error);
      } finally {
        setLoadingBlocos(false);
      }
    };

    carregarBlocos();
  }, []);

  useEffect(() => {
    const carregarGestores = async () => {
      setLoadingGestores(true);
      try {
        const gestoresData = await findManagers("", [1, 2, 3]);
        setGestores(gestoresData || []);

        if (user?.detail?.gestor && gestoresData) {
          const gestorDoUsuario = gestoresData.find(
            (g: any) => g._id === user.detail.gestor
          );
          if (gestorDoUsuario) {
            const nomeGestor =
              gestorDoUsuario.nome_guerra ||
              gestorDoUsuario.nome ||
              gestorDoUsuario.name ||
              gestorDoUsuario.extra?.nome_guerra ||
              gestorDoUsuario.extra?.nome ||
              gestorDoUsuario._id;
            setValue("GESTOR_ATUAL_NOME", nomeGestor);
          }
        }
      } catch (error) {
        console.error("Erro ao carregar gestores:", error);
      } finally {
        setLoadingGestores(false);
      }
    };

    carregarGestores();
  }, [user, setValue]);

  useEffect(() => {
    if (blocoSelecionadoNome && blocosConexao.length > 0) {
      const bloco = blocosConexao.find((p) => p.title === blocoSelecionadoNome);
      setBlocoSelecionado(bloco || null);
      setValue("INDICADOR_NOME", "");
    }
  }, [blocoSelecionadoNome, blocosConexao, setValue]);

  // Preparar dados para os componentes
  const blocosOptions = blocosConexao.map((bloco) => ({
    value: bloco.title,
    label: bloco.title,
  }));

  const indicadoresOptions = blocoSelecionado
    ? Object.entries(blocoSelecionado.strategicDrivers || {}).map(
        ([key, value]) => ({
          value: value,
          label: `${key}. ${value}`,
        })
      )
    : [];

  const nextStep = async () => {
    let fieldsToValidate: (keyof FormDataConexaoType)[] = [];

    if (currentStep === 1) {
      fieldsToValidate = [
        "TITULO",
        "BLOCO",
        "INDICADOR_NOME",
        "inicioEntrega",
        "conclusaoEntrega",
        "GESTOR_ATUAL_NOME",
      ];
    }

    const isValid = await trigger(fieldsToValidate);
    if (isValid && currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep === 1 && isEditMode) {
      navigate("/listagem");
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const mapFormDataToPayload = (data: FormDataConexaoType) => {
    const indicadorCod =
      blocoSelecionado?.strategicDrivers?.[data.INDICADOR_NOME] || "";

    const gestorSelecionado = gestores.find((g) => {
      const nomeGestor =
        g.nome_guerra ||
        g.nome ||
        g.name ||
        g.extra?.nome_guerra ||
        g.extra?.nome ||
        g._id;
      return nomeGestor === data.GESTOR_ATUAL_NOME;
    });

    // Converter datas para timestamp em milissegundos
    const startTimestamp = new Date(data.inicioEntrega).getTime();
    const endTimestamp = new Date(data.conclusaoEntrega).getTime();

    const payload: any = {
      title: data.TITULO,
      description: data.DESCRICAO,
      result: data.RESULTADO || "",
      created: { $date: new Date().toISOString() },
      start: startTimestamp,
      end: endTimestamp,
      indicator: {
        nome: data.INDICADOR_NOME,
        cod: indicadorCod,
        bloco: data.BLOCO,
      },
      categoryPremio: "CONEXAO",
      owner: user?.extra?.chave || user?._id,
      prefixo: user?.extra?.prefixo,
      diretoria: user?.extra?.diretoria,
      owner_cad_missao: user?.extra?.cad_missao,
      owner_cod_etapa: user?.extra?.cod_etapa,
      owner_funcao_localizacao: user?.extra?.funcao_localizacao,
      original_manager: gestorSelecionado?.chave || data.GESTOR_ATUAL_NOME,
      status: "aguardando",
      preRegistration: true,
    };

    // Adicionar _id quando estiver editando
    if (isEditMode && editingId) {
      payload._id = editingId;
    }

    return payload;
  };

  const handleFinalSubmit = async () => {
    if (isSubmitting) return;
    const isValid = await trigger(["DESCRICAO"]);
    if (!isValid) return;

    setIsSubmitting(true);
    try {
      const payload = mapFormDataToPayload(formData);
      console.log("Payload para envio:", payload);

      let response;
      if (isEditMode && editingId) {
        // Modo de edição - usar PUT
        response = await atualizarIniciativa(payload);
      } else {
        // Modo de criação - usar POST
        response = await salvarIniciativa(payload);
      }

      if (response?.message) {
        alert(response.message);
        return;
      }

      setShowSuccess(true);
    } catch (error: any) {
      console.error("Erro ao salvar iniciativa:", error);

      const mensagemErro =
        error?.response?.data?.message ||
        error?.message ||
        "Erro ao salvar a iniciativa. Tente novamente.";

      alert(mensagemErro);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
   return (
        <SuccessCardELBB
          onClose={() => navigate("/listagem")}
          type="CONEXAO"
        />
      );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-bb via-blue-bb-1 to-blue-bb-5 overflow-hidden relative">
      <StarDecorations
        count={18}
        density="medium"
        includeEmojis={true}
        includeCrosses={false}
      />

      <div className="flex flex-col items-start container px-6 my-8 mx-auto">
        <h1 className="text-green-bb-2 text-3xl font-bold mb-6">
          {isEditMode ? "Editar iniciativa" : "Cadastrar iniciativa"}
        </h1>

        <div className="bg-white-bb rounded-2xl p-8 px-6 md:px-8 lg:px-12 xl:px-20 w-full shadow-2xl relative">
          <StepIndicator
            currentStep={currentStep}
            steps={[
              { icon: Step1Icon, number: 1 },
              { icon: currentStep === 2 ? Step2Icon : Step3Icon, number: 2 },
            ]}
          />

          <form className="mt-8">
            <FormStep stepNumber={1} isActive={currentStep === 1}>
              <FormHeader
                title="Faça a pré-inscrição da sua iniciativa negocial no"
                subtitle="Prêmio BB"
                stepTitle="Informações da Iniciativa:"
                characterImage={characterFormELBB}
              />

              <div className="space-y-4">
                <FormField
                  label="Nome da Iniciativa"
                  type="text"
                  placeholder="Digite o nome da iniciativa"
                  maxLength={150}
                  showCounter
                  value={formData.TITULO}
                  error={errors.TITULO}
                  register={register("TITULO")}
                />

                <SelectField
                  label="Bloco do Conexão"
                  options={blocosOptions}
                  loading={loadingBlocos}
                  placeholder="Selecione um bloco"
                  error={errors.BLOCO}
                  register={register("BLOCO")}
                />

                <SelectField
                  label="Indicador"
                  options={indicadoresOptions}
                  disabled={!blocoSelecionado}
                  placeholder={
                    !blocoSelecionado
                      ? "Selecione primeiro um bloco"
                      : "Selecione..."
                  }
                  error={errors.INDICADOR_NOME}
                  register={register("INDICADOR_NOME")}
                />

                <DateRangeInputs
                  startLabel="Início da entrega"
                  endLabel="Conclusão da entrega"
                  minDate="2025-01-01"
                  maxDate="2025-12-31"
                  startRegister={register("inicioEntrega")}
                  endRegister={register("conclusaoEntrega")}
                  errors={{
                    start: errors.inicioEntrega,
                    end: errors.conclusaoEntrega,
                  }}
                />

                <ManagerSelector
                  managers={gestores}
                  loading={loadingGestores}
                  error={errors.GESTOR_ATUAL_NOME}
                  value={formData.GESTOR_ATUAL_NOME}
                  onChange={(val) => setValue("GESTOR_ATUAL_NOME", val, { shouldValidate: true })}
                />
              </div>
            </FormStep>

            <FormStep stepNumber={2} isActive={currentStep === 2}>
              <FormTextArea
                label="Informações detalhadas:"
                description={`Por que sua iniciativa negocial foi significativa para o alcance do indicador <span class="text-blue-bb">"${
                  formData.INDICADOR_NOME || "Selecione o bloco"
                }"</span>, e que resultado ela gerou?`}
                rows={12}
                maxLength={2000}
                showCounter
                placeholder="Descreva detalhadamente sua iniciativa..."
                value={formData.DESCRICAO}
                error={errors.DESCRICAO}
                register={register("DESCRICAO")}
              />
            </FormStep>

            <FormNavigation
              currentStep={currentStep}
              totalSteps={2}
              onPrev={prevStep}
              onNext={nextStep}
              onSubmit={handleFinalSubmit}
              isLastStep={currentStep === 2}
              hideBackButton={currentStep === 1 && !isEditMode}
              isSubmitting={isSubmitting}
            />
          </form>
        </div>
      </div>
    </div>
  );
}
